import { useState } from 'react';
import ArtZipButton from './ArtZipButton';
import BatchCompletion from './BatchCompletion';
import SourceSheetLink from './SourceSheetLink';

const GREENLIGHT_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1kBArkAJRKFWj7816T4cFyaswbZXVU3zuvOhNNqPI5lo/edit?gid=367350080#gid=367350080';

const ISSUE_LABELS = {
  not_found: 'No MSE order found',
  multi_match: 'Matches multiple orders',
  bad_status: 'Wrong status',
  bad_date: 'Missing/invalid date',
  date_conflict: 'Conflicting dates in paste',
};

function localBatchDate() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function rowStyle(row) {
  if (row.valid) return '';
  if (row.issues.includes('not_found')) return 'bg-gray-50 opacity-60';
  if (row.issues.includes('multi_match')) return 'bg-red-50';
  if (row.issues.includes('bad_status')) return 'bg-amber-50';
  return 'bg-red-50';
}

export default function GreenlightModal({ onClose, onComplete, secretKey, onNeedAuth, onAuthFailed }) {
  const [step, setStep] = useState('input'); // input → preview → applied
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [batch, setBatch] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmSubset, setConfirmSubset] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setConfirmSubset(false);
    try {
      const res = await fetch('/api/greenlight/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, batchDate: localBatchDate() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      setRows(data.rows);
      setSummary(data.summary);
      setBatch(data.batch);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (mode) => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/greenlight/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, secret: secretKey, mode, batchDate: localBatchDate() }),
      });
      const data = await res.json();
      if (res.status === 403) {
        onAuthFailed();
        return;
      }
      if (res.status === 409) {
        // Data changed between preview and apply — show refreshed validation
        setRows(data.rows);
        setSummary(data.summary);
        setConfirmSubset(false);
        setConfirmAll(false);
        setError(data.error);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Apply failed');
      setResult(data);
      setStep('applied');
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const invalidRows = rows ? rows.filter((r) => !r.valid) : [];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Greenlight MSE Batch</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === 'input' && 'Paste the greenlit dealer list from the Capital One sheet'}
              {step === 'preview' && 'Review matches and checks before applying'}
              {step === 'applied' && 'Batch applied to orders'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Step 1: Paste */}
          {step === 'input' && (
            <div>
              <SourceSheetLink href={GREENLIGHT_SHEET_URL} tone="emerald" label="to see the latest orders." />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={12}
                placeholder={'Paste dealer IDs and greenlight dates, one per line:\n\n20773\t2026-08-12\n39027\t2026-08-12\n42044\t2026-08-12'}
                autoFocus
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 resize-y"
              />
              <p className="text-xs text-gray-400 mt-2">
                Each dealer must have an MSE order in <span className="font-mono">codes_completed</span> status.
                Header rows and duplicate lines are handled automatically.
              </p>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && rows && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-gray-400 font-medium">Dealers</p>
                  <p className="text-xl font-bold text-gray-900">{summary.requested}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-emerald-600 font-medium">Ready</p>
                  <p className="text-xl font-bold text-emerald-700">{summary.valid}</p>
                </div>
                <div className={`rounded-lg px-4 py-3 ${summary.invalid > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <p className={`text-xs font-medium ${summary.invalid > 0 ? 'text-red-500' : 'text-gray-400'}`}>Issues</p>
                  <p className={`text-xl font-bold ${summary.invalid > 0 ? 'text-red-600' : 'text-gray-500'}`}>{summary.invalid}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-indigo-500 font-medium">Batch</p>
                  <p className="text-sm font-bold text-indigo-700 font-mono mt-1.5">MSE{batch?.date}_{summary.valid}</p>
                </div>
              </div>

              {/* All-clear / issues banner */}
              {summary.invalid === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 mb-4 text-sm text-emerald-800 font-medium">
                  ✓ All {summary.valid} dealer{summary.valid !== 1 ? 's' : ''} matched and in codes_completed — ready to greenlight
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4 text-sm text-red-800">
                  <span className="font-semibold">Review required:</span>
                  {summary.notFound > 0 && ` ${summary.notFound} not found.`}
                  {summary.badStatus > 0 && ` ${summary.badStatus} in wrong status.`}
                  {summary.multiMatch > 0 && ` ${summary.multiMatch} matching multiple orders.`}
                  {summary.badDate > 0 && ` ${summary.badDate} with date problems.`}
                  {' '}Fix the paste and re-check, or apply the valid subset below.
                </div>
              )}

              {/* Preview table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[38vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Dealer</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Greenlight</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Order</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Client</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Current Status</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Check</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((row, i) => (
                        <tr key={i} className={rowStyle(row)}>
                          <td className="px-4 py-2 font-mono text-xs font-semibold text-gray-900">{row.dealerId}</td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">{row.date || row.rawDate || '—'}</td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-700">
                            {row.orders.length === 0 ? '—' : row.orders.map((o) => o.order_uid).join(', ')}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500">
                            {row.orders.length === 0 ? '—' : row.orders.map((o) => o.client).join(', ')}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">
                            {row.orders.length === 0 ? '—' : row.orders.map((o) => o.status).join(', ')}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {row.valid ? (
                              <span className="text-emerald-600 font-medium">Ready</span>
                            ) : (
                              row.issues.map((issue) => (
                                <span key={issue} className="inline-block mr-1 mb-0.5 text-[10px] font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                  {ISSUE_LABELS[issue] || issue}
                                </span>
                              ))
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </>
          )}

          {/* Step 3: Applied */}
          {step === 'applied' && result && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Batch Greenlit</h3>
              <p className="text-sm font-mono text-gray-500 mb-6">{result.batch}</p>
              <div className="grid grid-cols-2 gap-8 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{result.applied}</p>
                  <p className="text-xs text-gray-400">Greenlit</p>
                </div>
                <div>
                  <p className={`text-2xl font-bold ${result.skipped.length > 0 ? 'text-amber-600' : 'text-gray-900'}`}>{result.skipped.length}</p>
                  <p className="text-xs text-gray-400">Skipped</p>
                </div>
              </div>
              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-xs text-emerald-800 space-y-1">
                <p>✓ Batch <span className="font-mono font-semibold">{result.batch}</span> created</p>
                <p>✓ {result.updated ?? result.applied} order{(result.updated ?? result.applied) !== 1 ? 's' : ''} updated: status → <span className="font-mono font-semibold">production</span></p>
                <p>✓ send_authorized → <span className="font-mono font-semibold">TRUE</span></p>
                <p>✓ greenlight_date set from paste</p>
              </div>
              {result.skipped.length > 0 && (
                <p className="text-xs text-amber-700 mt-2 max-w-md text-center font-mono">
                  Skipped: {result.skipped.map((s) => s.dealerId).join(', ')}
                </p>
              )}
              {result.logged === false && (
                <p className="text-xs text-red-600 mt-2 max-w-md text-center">
                  Note: batch history logging failed (greenlight_batches table missing?). Orders were updated successfully.
                </p>
              )}
              <BatchCompletion
                batches={[{ label: result.batch, orders: result.applied }]}
                batchDate={localBatchDate()}
                secretKey={secretKey}
                onNeedAuth={onNeedAuth}
                onAuthFailed={onAuthFailed}
                onSent={onComplete}
              />
            </div>
          )}
        </div>

        {/* Full-batch confirmation — states exactly what will change before writing */}
        {step === 'preview' && confirmAll && (
          <div className="mx-6 mt-2 bg-emerald-50 border border-emerald-300 rounded-xl px-5 py-4 min-h-0 overflow-y-auto max-h-[35vh]">
            <p className="text-sm font-semibold text-emerald-900 mb-1.5">
              Create batch <span className="font-mono">MSE{batch?.date}_{summary.valid}</span>?
            </p>
            <p className="text-xs text-emerald-800">
              This updates {summary.valid} order{summary.valid !== 1 ? 's' : ''}: status →{' '}
              <span className="font-mono font-semibold">production</span>, send_authorized →{' '}
              <span className="font-mono font-semibold">TRUE</span>, greenlight date from the paste,
              and stamps the batch label.
            </p>
          </div>
        )}

        {/* Subset confirmation — pinned above the footer buttons so it's always visible */}
        {step === 'preview' && confirmSubset && (
          <div className="mx-6 mb-0 mt-2 bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 min-h-0 overflow-y-auto max-h-[35vh]">
            <p className="text-sm font-semibold text-amber-900 mb-1.5">
              Apply partial batch — {summary.valid} of {summary.requested} dealers?
            </p>
            <p className="text-xs text-amber-800 mb-2">
              {invalidRows.length !== 1 ? `These ${invalidRows.length} dealers` : 'This dealer'} will be <span className="font-semibold">skipped</span> and not authorized:
            </p>
            <p className="text-xs font-mono text-amber-900">
              {invalidRows.map((r) => `${r.dealerId} (${r.issues.map((i) => ISSUE_LABELS[i] || i).join(', ')})`).join('; ')}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
          {step === 'input' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={!text.trim() || loading}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Checking...' : 'Check Batch'}
              </button>
            </>
          )}
          {step === 'preview' && confirmAll && (
            <>
              <button
                onClick={() => setConfirmAll(false)}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApply('all')}
                disabled={loading}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-40 transition-colors"
              >
                {loading ? 'Applying...' : `Yes — greenlight ${summary.valid} dealer${summary.valid !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 'preview' && !confirmAll && confirmSubset && (
            <>
              <button
                onClick={() => setConfirmSubset(false)}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApply('subset')}
                disabled={loading}
                className="px-5 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-40 transition-colors"
              >
                {loading ? 'Applying...' : `Yes — greenlight ${summary.valid}, skip ${invalidRows.length}`}
              </button>
            </>
          )}
          {step === 'preview' && !confirmAll && !confirmSubset && (
            <>
              <button
                onClick={() => { setStep('input'); setError(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Back
              </button>
              {summary.invalid > 0 && summary.valid > 0 && (
                <button
                  onClick={() => setConfirmSubset(true)}
                  disabled={loading}
                  className="px-4 py-2 border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium rounded-lg hover:bg-amber-100 disabled:opacity-40 transition-colors"
                >
                  Apply valid subset ({summary.valid} of {summary.requested})…
                </button>
              )}
              <button
                onClick={() => setConfirmAll(true)}
                disabled={loading || summary.invalid > 0 || summary.valid === 0}
                title={summary.invalid > 0 ? 'Fix all issues to greenlight the full batch' : undefined}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {`Greenlight ${summary.valid} Dealer${summary.valid !== 1 ? 's' : ''}…`}
              </button>
            </>
          )}
          {step === 'applied' && (
            <>
              <a
                href={`/api/greenlight/export/${encodeURIComponent(result.batch)}`}
                download
                className="px-4 py-2 border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm font-medium rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download CSV
              </a>
              <ArtZipButton
                label={result.batch}
                className="px-4 py-2 border border-indigo-300 bg-indigo-50 text-indigo-800 text-sm font-medium rounded-lg hover:bg-indigo-100 transition-colors"
              >
                Download Art ZIP
              </ArtZipButton>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
